import { ConversationPlatform } from "@redotech/redo-support-conversation-protocol-common/conversation-protocol";
import { assertNever } from "@redotech/util/type";
import { Schema } from "effect";
import type { ShopperAiIntegrationType } from "../integration/integration";
import type { MessageChannel } from "./message";
import { MESSAGE_CHANNELS } from "./message-channels";

const MESSAGE_CHANNEL_SET: ReadonlySet<string> = new Set(MESSAGE_CHANNELS);

/** Type guard narrowing an arbitrary string to a V2 {@link MessageChannel}. */
export const isMessageChannel = (value: string): value is MessageChannel =>
  MESSAGE_CHANNEL_SET.has(value);

/**
 * Human-readable label for a V2 {@link MessageChannel}.
 *
 * The exhaustive switch is the compile-time gate: adding a new literal to
 * {@link MESSAGE_CHANNELS} without a label here fails type-check, since the
 * unhandled case leaves `channel` non-`never` at the {@link assertNever} call.
 */
export const messageChannelDisplayName = (channel: MessageChannel): string => {
  switch (channel) {
    case "gmail":
      return "Gmail";
    case "outlook":
      return "Outlook";
    case "customEmail":
      return "Custom Email";
    case "redoChat":
      return "Redo chat";
    case "facebookDm":
      return "Facebook";
    case "facebookComments":
      return "Facebook comments";
    case "instagramDm":
      return "Instagram DM";
    case "instagramComments":
      return "Instagram comments";
    case "sms":
      return "SMS";
    case "voice":
      return "Voice";
    case "attentive":
      return "Attentive";
    case "postscript":
      return "Postscript";
    case "tiktok":
      return "TikTok";
    case "contactForm":
      return "Contact Form";
    case "internalNote":
      return "Internal note";
    case "mobileMessage":
      return "Mobile Message";
    default:
      return assertNever(channel);
  }
};

export type EmailProviderType = "gmail" | "outlook" | "custom_email";

/** Merchant/chat-flow "send as" values; `custom` maps to integration type `custom_email`. */
export type EmailSendAsIntegrationKind = "gmail" | "outlook" | "custom";

export const emailProviderTypeToSendAsIntegrationKind = (
  integrationType: EmailProviderType,
): EmailSendAsIntegrationKind =>
  integrationType === "custom_email" ? "custom" : integrationType;

export const sendAsIntegrationKindToEmailProviderType = (
  integrationKind: EmailSendAsIntegrationKind,
): EmailProviderType =>
  integrationKind === "custom" ? "custom_email" : integrationKind;

export const isEmailIntegrationType = (
  integrationType: ShopperAiIntegrationType,
): integrationType is EmailProviderType => {
  return (
    integrationType === "gmail" ||
    integrationType === "outlook" ||
    integrationType === "custom_email"
  );
};

export type OAuthEmailIntegrationType = "gmail" | "outlook";

export const isOAuthEmailIntegrationType = (
  integrationType: ShopperAiIntegrationType,
): integrationType is OAuthEmailIntegrationType =>
  isEmailIntegrationType(integrationType) && integrationType !== "custom_email";

export const SMS_INTEGRATION_TYPES = [
  "telnyx_sms",
  "imessage",
  "rcs",
  "sms_phone_number",
  "shared_sms",
] as const satisfies readonly ShopperAiIntegrationType[];

export type SmsIntegrationType = (typeof SMS_INTEGRATION_TYPES)[number];

const SMS_INTEGRATION_TYPE_SET: ReadonlySet<string> = new Set(
  SMS_INTEGRATION_TYPES,
);

export const isSmsIntegrationType = (
  integrationType: ShopperAiIntegrationType,
): integrationType is SmsIntegrationType =>
  SMS_INTEGRATION_TYPE_SET.has(integrationType);

export const integrationTypeToChannel = {
  gmail: "gmail",
  outlook: "outlook",
  custom_email: "customEmail",
  instagram: "instagramDm",
  facebook: "facebookDm",
  tiktok: "tiktok",
  telnyx_sms: "sms",
  sms_phone_number: "sms",
  shared_sms: "sms",
  imessage: "sms",
  rcs: "sms",
  gorgias_sms: "sms",
  richpanel_sms: "sms",
  telnyx_voice: "voice",
  mobileMessage: "mobileMessage",
  attentive: "attentive",
  postscript: "postscript",
  redoChatAi: "redoChat",
  redoChatFlow: "redoChat",
  redoChatLive: "redoChat",
  contactForm: "contactForm",
} as const satisfies Record<ShopperAiIntegrationType, MessageChannel>;

/** The hot-swap equivalence class of an integration: the set of types you can
 * swap between transparently. Persisted on the thread since the type is not durable. */
export const INTEGRATION_KINDS = [
  "email",
  "dedicated_sms",
  "pooled_sms",
  "third_party_sms",
  "imessage",
  "voice",
  "facebook",
  "instagram",
  "tiktok",
  "contact_form",
  "attentive",
  "postscript",
  "mobile_message",
  // redoChat subtypes are distinct products, not hot-swap peers, so each is its
  // own kind. `redo_chat` is deprecated (decodes pre-split rows until backfilled).
  "redo_chat",
  "redo_chat_ai",
  "redo_chat_flow",
  "redo_chat_live",
] as const;

export type IntegrationKind = (typeof INTEGRATION_KINDS)[number];

/** Schema for DB decode of `channel_thread.kind`, derived from the one list. */
export const IntegrationKindSchema: Schema.Schema<IntegrationKind> =
  Schema.Literal(...INTEGRATION_KINDS);

// email/dedicated_sms collapse across providers and redo_chat across modes; the
// rest are 1:1 but still need a kind since reconnecting swaps the row same-type.
export const integrationTypeToKind = {
  gmail: "email",
  outlook: "email",
  custom_email: "email",
  telnyx_sms: "dedicated_sms",
  sms_phone_number: "dedicated_sms",
  shared_sms: "pooled_sms",
  imessage: "imessage",
  rcs: "dedicated_sms",
  // Third-party bridges (Gorgias/Richpanel own the number; Redo has no phone
  // handle and never sends outbound), so they resolve per-account, not by handle.
  gorgias_sms: "third_party_sms",
  richpanel_sms: "third_party_sms",
  telnyx_voice: "voice",
  facebook: "facebook",
  instagram: "instagram",
  tiktok: "tiktok",
  contactForm: "contact_form",
  attentive: "attentive",
  postscript: "postscript",
  mobileMessage: "mobile_message",
  redoChatAi: "redo_chat_ai",
  redoChatFlow: "redo_chat_flow",
  redoChatLive: "redo_chat_live",
} as const satisfies Record<ShopperAiIntegrationType, IntegrationKind>;

export const kindForIntegrationType = (
  integrationType: ShopperAiIntegrationType,
): IntegrationKind => integrationTypeToKind[integrationType];

/** What a kind's `resolution_key` normalizes to. `account` keys resolve per
 * (account, kind); the rest resolve by the merchant handle shown. */
export type ResolveBy =
  | "mailbox"
  | "phone"
  | "pageId"
  | "igAccountId"
  | "shopId"
  | "account";

export const kindToResolveBy = {
  email: "mailbox",
  dedicated_sms: "phone",
  voice: "phone",
  facebook: "pageId",
  instagram: "igAccountId",
  tiktok: "shopId",
  pooled_sms: "account",
  third_party_sms: "account",
  imessage: "account",
  contact_form: "account",
  attentive: "account",
  postscript: "account",
  mobile_message: "account",
  redo_chat: "account",
  redo_chat_ai: "account",
  redo_chat_flow: "account",
  redo_chat_live: "account",
} as const satisfies Record<IntegrationKind, ResolveBy>;

/** True when a kind resolves per-account (pooled/app-owned), false when it
 * resolves by a dedicated merchant handle (mailbox/number/page/shop). */
export const kindResolvesByAccount = (kind: IntegrationKind): boolean =>
  kindToResolveBy[kind] === "account";

/** Inverse of {@link integrationTypeToChannel}; derived so the two can't drift. */
export const CHANNEL_TO_INTEGRATION_TYPES: Readonly<
  Record<MessageChannel, readonly ShopperAiIntegrationType[]>
> = (() => {
  const inverse = Object.fromEntries(
    MESSAGE_CHANNELS.map((channel) => [
      channel,
      [] as ShopperAiIntegrationType[],
    ]),
  ) as Record<MessageChannel, ShopperAiIntegrationType[]>;
  for (const [integrationType, channel] of Object.entries(
    integrationTypeToChannel,
  )) {
    inverse[channel as MessageChannel].push(
      integrationType as ShopperAiIntegrationType,
    );
  }
  return inverse;
})();

/** Integration types that can carry `channel` (see {@link CHANNEL_TO_INTEGRATION_TYPES}). */
export const integrationTypesForChannel = (
  channel: MessageChannel,
): readonly ShopperAiIntegrationType[] => CHANNEL_TO_INTEGRATION_TYPES[channel];

/** Inverse of {@link integrationTypeToKind}; derived so the two can't drift.
 * Every kind has ≥1 type since `integrationTypeToKind` is total and onto. */
export const INTEGRATION_TYPES_BY_KIND: Readonly<
  Record<IntegrationKind, readonly ShopperAiIntegrationType[]>
> = (() => {
  const inverse = Object.fromEntries(
    INTEGRATION_KINDS.map((kind) => [kind, [] as ShopperAiIntegrationType[]]),
  ) as Record<IntegrationKind, ShopperAiIntegrationType[]>;
  for (const [integrationType, kind] of Object.entries(integrationTypeToKind)) {
    inverse[kind as IntegrationKind].push(
      integrationType as ShopperAiIntegrationType,
    );
  }
  return inverse;
})();

/** Integration types a send resolves against, keyed by the thread's own kind
 * (the hot-swap equivalence class), independent of the message channel. */
export const integrationTypesForKind = (
  kind: IntegrationKind,
): readonly ShopperAiIntegrationType[] => INTEGRATION_TYPES_BY_KIND[kind];

/** True when a message on this channel goes outbound through a merchant
 * integration. Internal notes stay inside Redo and have no integration. */
export const channelSendsThroughIntegration = (
  channel: MessageChannel,
): boolean => channel !== "internalNote";

/**
 * The message channels backed by an email provider. Single source of truth for
 * "is this an email channel": adding a provider here updates `EmailChannel` and
 * every exhaustive (`assertNever`) switch over it.
 */
export const EMAIL_CHANNELS = ["gmail", "outlook", "customEmail"] as const;

export type EmailChannel = (typeof EMAIL_CHANNELS)[number];

/**
 * Helper to check if a channel is an email channel (gmail, outlook, or customEmail)
 */
export const isEmailChannel = (channel: string): channel is EmailChannel => {
  return (
    channel === "gmail" || channel === "outlook" || channel === "customEmail"
  );
};

/** The only channels subject to Meta's messaging window. */
export const META_DM_CHANNELS = ["facebookDm", "instagramDm"] as const;

export type MetaDmChannel = (typeof META_DM_CHANNELS)[number];

export const isMetaDmChannel = (
  channel: MessageChannel,
): channel is MetaDmChannel =>
  channel === "facebookDm" || channel === "instagramDm";

/**
 * Social media platform channels (Meta DMs/comments and TikTok). These channels
 * only support plain-text outbound replies — the underlying platform APIs reject
 * or strip rich-text formatting, inline links, and file attachments — so the
 * composer hides those affordances when replying on one of them.
 */
export const isSocialChannel = (
  channel: MessageChannel,
): channel is
  | "facebookDm"
  | "facebookComments"
  | "instagramDm"
  | "instagramComments"
  | "tiktok" => {
  return (
    channel === "facebookDm" ||
    channel === "facebookComments" ||
    channel === "instagramDm" ||
    channel === "instagramComments" ||
    channel === "tiktok"
  );
};

/**
 * Social channels that accept an inline image on outbound replies. Only Facebook
 * (DMs and comments) supports sending an outbound image today — the Instagram and
 * TikTok APIs reject image replies — so the composer only exposes the image
 * button on Facebook channels and hides it on Instagram and TikTok.
 */
export const channelSupportsOutboundImage = (
  channel: MessageChannel,
): channel is "facebookDm" | "facebookComments" => {
  return channel === "facebookDm" || channel === "facebookComments";
};

/**
 * Whether a channel can carry inline body images — `<img>` nodes embedded in the
 * message body (sent as `cid:` MIME parts on email, rendered inline on Redo
 * chat). Mirrors which composers expose an inline-image button: email and Redo
 * chat always support it, Facebook (DMs and comments) accepts a single outbound
 * image, and every other channel (Instagram, TikTok, SMS, voice, attentive,
 * postscript, contact form, mobile message) rejects inline images. Used to keep
 * a macro that contains inline images from being applied on a channel that would
 * silently drop them.
 */
export const channelSupportsInlineImages = (
  channel: MessageChannel,
): boolean => {
  return (
    isEmailChannel(channel) ||
    channel === "redoChat" ||
    channelSupportsOutboundImage(channel)
  );
};

/**
 * Channels the AI reply agent can handle. Guards the client-side auto-trigger
 * so we never send a request the server-side resolver will reject.
 */
const AI_REPLY_CHANNELS = new Set<MessageChannel>([
  "gmail",
  "outlook",
  "customEmail",
  "redoChat",
  "facebookDm",
  "instagramDm",
  "tiktok",
  "sms",
  "mobileMessage",
  "contactForm",
]);

export const isAiReplyChannel = (channel: MessageChannel): boolean =>
  AI_REPLY_CHANNELS.has(channel);

const messageChannelToConversationPlatformMap: Partial<
  Record<MessageChannel, ConversationPlatform>
> = {
  gmail: ConversationPlatform.EMAIL,
  outlook: ConversationPlatform.EMAIL,
  customEmail: ConversationPlatform.EMAIL,
  redoChat: ConversationPlatform.REDO_CHAT,
  facebookDm: ConversationPlatform.FACEBOOK,
  facebookComments: ConversationPlatform.FACEBOOK_COMMENTS,
  instagramDm: ConversationPlatform.INSTAGRAM,
  instagramComments: ConversationPlatform.INSTAGRAM_COMMENTS,
  sms: ConversationPlatform.SMS,
  voice: ConversationPlatform.VOICE,
  attentive: ConversationPlatform.ATTENTIVE,
  postscript: ConversationPlatform.POSTSCRIPT,
  tiktok: ConversationPlatform.TIKTOK,
  mobileMessage: ConversationPlatform.MOBILE_MESSAGE,
};

export const messageChannelToConversationPlatform = (
  channel: MessageChannel,
): ConversationPlatform | undefined =>
  messageChannelToConversationPlatformMap[channel];
